/**
 * PurchaseRegisterProcessingController — page Tenue du registre des achats
 * (`/purchases/register-processing.html`, EPIC-018 / 18.4 — §8.bis.C).
 *
 * Charge la file des factures §8 `validated` non encore traitées, polling
 * lignes EN16931 par facture, scellement bulk transactionnel multi-factures
 * avec idempotency-key UUID v4 généré côté client, ou marquage « sans
 * marchandise » par facture.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS —
 * uniquement clone de `<template>` + remplissage `data-slot`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const PAYMENT_METHOD_DEFAULT = "virement";

const PAYMENT_METHOD_LABELS = {
  virement: "Virement",
  prelevement: "Prélèvement",
  carte: "Carte bancaire",
  especes: "Espèces",
  cheque: "Chèque",
  autre: "Autre",
};

export default class PurchaseRegisterProcessingController extends BaseController {
  static targets = [
    "pendingCount",
    "sealButton",
    "invoicesContainer",
    "emptyState",
    "confirmSealModal",
    "confirmNoMerchandiseModal",
  ];

  connect() {
    /**
     * @type {Map<string, {invoice: object, lines: object[], checked: Set<number>,
     *                     paymentMethod: string, sectionEl: HTMLElement}>}
     */
    this._invoiceState = new Map();
    this._pendingNoMerchandiseInvoiceId = null;
    this._sealing = false;

    this._fetchPending();

    this._onEntryCreated = () => this._fetchPending();
    window.addEventListener("purchase-register:entry-created", this._onEntryCreated);
  }

  disconnect() {
    if (this._onEntryCreated) {
      window.removeEventListener("purchase-register:entry-created", this._onEntryCreated);
    }
  }

  // ---------------------------------------------------------------------------
  // Data load
  // ---------------------------------------------------------------------------

  async _fetchPending() {
    this._invoicesContainerEl().textContent = "";
    this._invoiceState.clear();
    this._refreshGlobalCount();

    try {
      const response = await api.purchaseRegister.listPending();
      const invoices = Array.isArray(response.data) ? response.data : [];
      if (invoices.length === 0) {
        this._showEmpty(true);
        return;
      }
      this._showEmpty(false);
      for (const invoice of invoices) {
        this._renderInvoiceSection(invoice);
        this._loadInvoiceLines(invoice);
      }
    } catch (err) {
      this._handleApiError(err, "Impossible de charger la file des factures.");
    }
  }

  _renderInvoiceSection(invoice) {
    const fragment = this.cloneTemplate("tpl-purchase-invoice-section");
    this.fillSlots(fragment, {
      supplierName: invoice.supplierName || "—",
      pieceReference: invoice.pieceReference || "—",
      issuedAtLabel: this._formatDate(invoice.issuedAt),
      amountInclVat: this._formatAmount(invoice.amountInclVat),
      linesCheckedCount: "0",
      linesTotalCount: "0",
    });

    const sectionEl = fragment.firstElementChild;
    if (!sectionEl) return;
    sectionEl.dataset.invoiceId = invoice.invoiceId;

    const select = sectionEl.querySelector('[data-purchase-register-processing-target="paymentMethodSelect"]');
    if (select) {
      select.value = PAYMENT_METHOD_DEFAULT;
      select.dataset.invoiceId = invoice.invoiceId;
      select.addEventListener("change", (event) => this._onPaymentMethodChange(event));
    }
    const noMerchandiseBtn = sectionEl.querySelector('[data-action*="markNoMerchandise"]');
    if (noMerchandiseBtn) {
      noMerchandiseBtn.dataset.invoiceId = invoice.invoiceId;
    }

    this._invoicesContainerEl().appendChild(fragment);

    this._invoiceState.set(invoice.invoiceId, {
      invoice,
      lines: [],
      checked: new Set(),
      paymentMethod: PAYMENT_METHOD_DEFAULT,
      sectionEl,
    });
  }

  async _loadInvoiceLines(invoice) {
    const targetId = invoice.notafactoInvoiceId || invoice.invoiceId;
    try {
      const response = await api.purchaseRegister.getInvoiceLines(targetId);
      const lines = Array.isArray(response.lines) ? response.lines : [];
      this._renderLines(invoice, lines);
    } catch (err) {
      if (err instanceof APIError && err.status === 503) {
        this.showError(
          "Lignes indisponibles pour la facture " + (invoice.supplierName || "") + " (passerelle).",
        );
        return;
      }
      this._handleApiError(err, "Impossible de charger les lignes de la facture.");
    }
  }

  _renderLines(invoice, lines) {
    const state = this._invoiceState.get(invoice.invoiceId);
    if (!state) return;
    state.lines = lines;

    const tbody = state.sectionEl.querySelector("tbody[data-slot-element]")
      ?? state.sectionEl.querySelector("tbody");
    if (!tbody) return;
    tbody.textContent = "";

    for (const line of lines) {
      const fragment = this.cloneTemplate("tpl-purchase-invoice-line");
      this.fillSlots(fragment, {
        lineIndex: String(line.lineIndex),
        invoiceId: invoice.invoiceId,
        description: line.description || "",
        quantity: this._formatQuantity(line.quantity),
        unitPriceExclVat: this._formatAmount(line.unitPriceExclVat),
        totalAmountExclVat: this._formatAmount(line.totalAmountExclVat),
        vatAmount: this._formatAmount(line.vatAmount),
        totalAmountInclVat: this._formatAmount(line.totalAmountInclVat),
      });

      const checkbox = fragment.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = !!invoice.defaultChecked;
        checkbox.dataset.invoiceId = invoice.invoiceId;
        checkbox.dataset.lineIndex = String(line.lineIndex);
        checkbox.setAttribute("aria-label", "Inclure la ligne " + ((line.lineIndex ?? 0) + 1));
        if (invoice.defaultChecked) {
          state.checked.add(line.lineIndex);
        }
      }
      tbody.appendChild(fragment);
    }

    this._refreshSectionCount(invoice.invoiceId);
    this._refreshGlobalCount();
  }

  // ---------------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------------

  toggleLine(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;
    const invoiceId = checkbox.dataset.invoiceId;
    const lineIndex = Number(checkbox.dataset.lineIndex);
    if (!invoiceId || Number.isNaN(lineIndex)) return;
    const state = this._invoiceState.get(invoiceId);
    if (!state) return;
    if (checkbox.checked) {
      state.checked.add(lineIndex);
    } else {
      state.checked.delete(lineIndex);
    }
    this._refreshSectionCount(invoiceId);
    this._refreshGlobalCount();
  }

  checkAll() {
    for (const [invoiceId, state] of this._invoiceState) {
      state.checked = new Set(state.lines.map((l) => l.lineIndex));
      const checkboxes = state.sectionEl.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((cb) => { cb.checked = true; });
      this._refreshSectionCount(invoiceId);
    }
    this._refreshGlobalCount();
  }

  uncheckAll() {
    for (const [invoiceId, state] of this._invoiceState) {
      state.checked.clear();
      const checkboxes = state.sectionEl.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach((cb) => { cb.checked = false; });
      this._refreshSectionCount(invoiceId);
    }
    this._refreshGlobalCount();
  }

  _onPaymentMethodChange(event) {
    const select = event.target;
    const invoiceId = select?.dataset?.invoiceId;
    if (!invoiceId) return;
    const state = this._invoiceState.get(invoiceId);
    if (state) state.paymentMethod = select.value;
  }

  // ---------------------------------------------------------------------------
  // Seal bulk
  // ---------------------------------------------------------------------------

  openSealConfirmation() {
    if (this._totalChecked() === 0) {
      this.showError("Aucune ligne cochée à sceller.");
      return;
    }
    this._toggleModal(this.confirmSealModalTarget, true);
  }

  cancelSeal() {
    this._toggleModal(this.confirmSealModalTarget, false);
  }

  async confirmSeal() {
    if (this._sealing) return;
    this._toggleModal(this.confirmSealModalTarget, false);

    const items = this._buildSealItems();
    if (items.length === 0) {
      this.showError("Aucune ligne cochée à sceller.");
      return;
    }

    this._sealing = true;
    if (this.hasSealButtonTarget) this.sealButtonTarget.disabled = true;
    const idempotencyKey = this._generateUuid();

    try {
      const response = await api.purchaseRegister.sealBulk({
        idempotencyKey,
        items,
      });
      const n = response.entriesCreated ?? 0;
      this.showSuccess(
        n === 1
          ? "1 écriture scellée."
          : n + " écritures scellées.",
      );
      await this._fetchPending();
    } catch (err) {
      if (err instanceof APIError && err.status === 423) {
        this.showError(err.message || "Exercice clôturé : action refusée.");
      } else if (err instanceof APIError && err.status === 409) {
        this.showError(err.message || "Une facture a déjà été traitée.");
        await this._fetchPending();
      } else {
        this._handleApiError(err, "Échec du scellement.");
      }
    } finally {
      this._sealing = false;
      if (this.hasSealButtonTarget) this.sealButtonTarget.disabled = this._totalChecked() === 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Mark no merchandise
  // ---------------------------------------------------------------------------

  markNoMerchandise(event) {
    const btn = event.currentTarget;
    const invoiceId = btn?.dataset?.invoiceId;
    if (!invoiceId) return;
    this._pendingNoMerchandiseInvoiceId = invoiceId;
    this._toggleModal(this.confirmNoMerchandiseModalTarget, true);
  }

  cancelNoMerchandise() {
    this._pendingNoMerchandiseInvoiceId = null;
    this._toggleModal(this.confirmNoMerchandiseModalTarget, false);
  }

  async confirmNoMerchandise() {
    const invoiceId = this._pendingNoMerchandiseInvoiceId;
    this._toggleModal(this.confirmNoMerchandiseModalTarget, false);
    if (!invoiceId) return;
    this._pendingNoMerchandiseInvoiceId = null;

    try {
      await api.purchaseRegister.markNoMerchandise(invoiceId);
      this.showSuccess("Facture marquée comme traitée sans écriture.");
      await this._fetchPending();
    } catch (err) {
      if (err instanceof APIError && err.status === 409) {
        this.showError("Facture déjà traitée.");
        await this._fetchPending();
        return;
      }
      this._handleApiError(err, "Impossible de marquer la facture.");
    }
  }

  // ---------------------------------------------------------------------------
  // Manual entry : déléguée au controller frère
  // `purchase-register-manual-entry` (EPIC-018 / 18.5) — le bouton
  // « + Écriture manuelle » cible directement
  // `purchase-register-manual-entry#open`.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _invoicesContainerEl() {
    return this.hasInvoicesContainerTarget ? this.invoicesContainerTarget : this.element;
  }

  _showEmpty(visible) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = !visible;
    if (this.hasInvoicesContainerTarget) {
      this.invoicesContainerTarget.querySelectorAll(":scope > details").forEach((el) => {
        if (visible) el.remove();
      });
    }
  }

  _refreshSectionCount(invoiceId) {
    const state = this._invoiceState.get(invoiceId);
    if (!state) return;
    const checkedSlot = state.sectionEl.querySelector('[data-slot="linesCheckedCount"]');
    const totalSlot = state.sectionEl.querySelector('[data-slot="linesTotalCount"]');
    if (checkedSlot) checkedSlot.textContent = String(state.checked.size);
    if (totalSlot) totalSlot.textContent = String(state.lines.length);
  }

  _refreshGlobalCount() {
    const total = this._totalChecked();
    if (this.hasPendingCountTarget) this.pendingCountTarget.textContent = String(total);
    if (this.hasSealButtonTarget) this.sealButtonTarget.disabled = total === 0;
  }

  _totalChecked() {
    let total = 0;
    for (const state of this._invoiceState.values()) {
      total += state.checked.size;
    }
    return total;
  }

  _buildSealItems() {
    const items = [];
    for (const state of this._invoiceState.values()) {
      if (state.checked.size === 0) continue;
      const checkedLines = state.lines
        .filter((l) => state.checked.has(l.lineIndex))
        .map((l) => {
          const desc = this._readEditedDescription(state.sectionEl, l.lineIndex) ?? l.description;
          return {
            lineIndex: l.lineIndex,
            description: desc,
            amountExclVat: String(l.totalAmountExclVat ?? "0"),
            vatAmount: String(l.vatAmount ?? "0"),
            amountInclVat: String(l.totalAmountInclVat ?? "0"),
          };
        });
      items.push({
        invoiceId: state.invoice.invoiceId,
        lines: checkedLines,
        paymentMethod: state.paymentMethod || PAYMENT_METHOD_DEFAULT,
      });
    }
    return items;
  }

  _readEditedDescription(sectionEl, lineIndex) {
    const checkbox = sectionEl.querySelector(
      'input[type="checkbox"][data-line-index="' + lineIndex + '"]',
    );
    if (!checkbox) return null;
    const row = checkbox.closest("tr");
    if (!row) return null;
    const input = row.querySelector('input[type="text"]');
    return input ? input.value : null;
  }

  _toggleModal(modalEl, visible) {
    if (!modalEl) return;
    if (visible) {
      modalEl.showModal();
    } else {
      modalEl.close();
    }
  }

  _generateUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    // Fallback RFC 4122 v4 — Math.random suffit côté client (pas de crypto critique).
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _formatAmount(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  _formatQuantity(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString("fr-FR", { maximumFractionDigits: 4 });
  }

  _formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  }

  _handleApiError(err, fallbackMessage) {
    if (err instanceof APIError && err.status === 403 && err.code === "not_bic_vente_eligible") {
      this.showError("Cette section n'est pas disponible pour votre activité.");
      return;
    }
    const message = err instanceof APIError && err.message ? err.message : fallbackMessage;
    this.showError(message);
  }
}

// PAYMENT_METHOD_LABELS exporté implicitement via le scope module ; garder
// l'import si l'on veut formaliser l'affichage côté UI dans une future
// itération (libellés FR pour une éventuelle ventilation).
void PAYMENT_METHOD_LABELS;
